import { ComponentProps, forwardRef, useState } from "react";
import {
  Button,
  isWeb,
  styled,
  Text,
  Input as TGInput,
  YStack,
  type GetThemeValueForKey,
} from "tamagui";

import { ClosedEye, OpenEye } from "../../icons";
import { stylex } from "../../utils";

const getLableMaxWidth = (totalWidth: number, percent: number) => {
  return Math.round((totalWidth / 100) * percent);
};

type Variants = "inline" | "stacked" | "underlined";
type Size = "md" | "sm";

export const Input = styled(TGInput, {
  name: "Input",
  cursor: "text",
  width: "100%",
  minWidth: 0,
  variants: {
    variant: {
      underlined: {
        borderBottomColor: "$gray-secondary-400",
        borderBottomWidth: "$0.2",
        borderTopWidth: 0,
        borderLeftWidth: 0,
        borderRightWidth: 0,
        color: "$gray-600",
        fontFamily: "$subtitle",
        fontSize: "$2",
        fontWeight: "$4",
        lineHeight: "$2",
        paddingHorizontal: 0,
        focusStyle: {
          outlineWidth: 0,
          borderColor: "$lightblue-700",
          color: "$gray-primary-900",
        },
        disabledStyle: {
          cursor: "not-allowed",
          borderColor: "$gray-200",
          color: "$gray-200",
        },
      },
      inline: {
        borderColor: "$gray-secondary-400",
        borderWidth: "$0.2",
        color: "$gray-600",
        fontFamily: "$subtitle",
        fontSize: "$2",
        fontWeight: "$4",
        lineHeight: "$2",
        paddingHorizontal: "$3",
        focusStyle: {
          outlineWidth: 0,
          borderColor: "$lightblue-700",
          color: "$gray-primary-900",
        },
        disabledStyle: {
          cursor: "not-allowed",
          borderColor: "$gray-200",
          color: "$gray-200",
        },
      },
      stacked: {
        borderColor: "$gray-secondary-400",
        borderWidth: "$0.2",
        color: "$gray-600",
        fontFamily: "$subtitle",
        fontSize: "$2",
        fontWeight: "$4",
        lineHeight: "$2",
        paddingHorizontal: "$3",
        focusStyle: {
          outlineWidth: 0,
          borderColor: "$lightblue-700",
          color: "$gray-primary-900",
        },
        disabledStyle: {
          cursor: "not-allowed",
          borderColor: "$gray-200",
          color: "$gray-200",
        },
      },
    },
    size: {
      md: {
        height: "$7",
        width: "100%",
      },
      sm: {
        height: "$4",
        width: "100%",
      },
    },
    rounded: {
      md: {
        borderRadius: "$5",
      },
      none: {
        borderRadius: 0,
      },
    },
  } as const,
  defaultVariants: {
    variant: "stacked",
    size: "md",
    rounded: "md",
  },
});

type InputProps = ComponentProps<typeof Input>;

const Label = styled(Text, {
  name: "Label",
  ellipsizeMode: "tail",
  numberOfLines: 1,

  variants: {
    variant: {
      underlined: {
        display: "none",
      },
      inline: {
        color: "$gray-primary-900",
        position: "absolute",
        backgroundColor: "white",
        left: "$3",
        paddingHorizontal: 2,
        top: -14,
        disabledStyle: {
          cursor: "not-allowed",
          color: "$gray-secondary-400",
        },
      },
      stacked: {
        color: "$gray-primary-900",
        backgroundColor: "white",
        paddingLeft: "$1",
        paddingBottom: "$1",
        disabledStyle: {
          cursor: "not-allowed",
          color: "$gray-secondary-400",
        },
      },
    },
    font: {
      md: {
        fontFamily: "$body",
        fontSize: "$3",
        lineHeight: "$3",
        fontWeight: "$5",
      },
      sm: {
        fontFamily: "$body",
        fontSize: "$3",
        lineHeight: "$3",
        fontWeight: "$5",
      },
    },
  } as const,
  defaultVariants: {
    variant: "inline",
    font: "md",
  },
});

// changed from Label to Text coz fonts somehow weren't working with Label

const ErrorMessage = styled(Text, {
  name: "ErrorMessage",
  ellipsizeMode: "tail",
  numberOfLines: 1,
  variants: {
    variant: {
      underlined: {
        display: "none",
      },
      inline: {
        position: "absolute",
        left: "$3",
        backgroundColor: "white",
        paddingHorizontal: 2,
        color: "$error-500",
        fontFamily: "$subtitle",
        fontSize: "$1",
        lineHeight: "$1",
        fontWeight: "$4",
        bottom: -5.4,
      },
      stacked: {
        backgroundColor: "white",
        paddingHorizontal: 2,
        color: "$error-500",
        fontFamily: "$subtitle",
        fontSize: "$1",
        lineHeight: "$1",
        fontWeight: "$4",
        paddingTop: "$1",
        paddingLeft: "$1",
      },
    },
  } as const,
  defaultVariants: {
    variant: "inline",
  },
});

const BottomMessage = styled(Text, {
  name: "BottomMessage",
  ellipsizeMode: "tail",
  numberOfLines: 1,
  variants: {
    variant: {
      stacked: {
        backgroundColor: "white",
        paddingHorizontal: 2,
        color: "$gray-secondary-400",
        fontFamily: "$subtitle",
        fontSize: "$2",
        lineHeight: "$2",
        fontWeight: "$4",
        paddingTop: "$0.5",
        paddingLeft: "$3",
      },
    },
  } as const,
  defaultVariants: {
    variant: "stacked",
  },
});

type InputRef = React.ElementRef<typeof Input>;

const resolveShowPasswordButtonYAxisPosition = (
  size: Size,
  isMessage: boolean,
  isError: boolean,
) => {
  const buttonHeight = 20;
  const inputHeight = size === "md" ? 52 : 32;
  const bottomMessageHeight = isError ? 0 : isMessage ? 20 : 0;

  return (inputHeight - buttonHeight) / 2 + bottomMessageHeight;
};

export const InputWithLabel = forwardRef<
  InputRef,
  InputProps & {
    isMessage?: boolean;
    message?: string;
    label?: string;
    isError?: boolean;
    error?: string;
    disabled?: boolean;
    size?: Size;
    maxWidth?: number | "unset" | GetThemeValueForKey<"maxWidth">;
    variant?: Variants;
  }
>(
  (
    {
      label,
      isError,
      isMessage,
      message,
      maxWidth,
      size = "md",
      error,
      disabled,
      variant,
      secureTextEntry = false,
      ...rest
    },
    ref,
  ) => {
    const [passVisible, setPassVisible] = useState(secureTextEntry);
    const [inputFrame, setInputFrame] = useState({
      width: 0,
    });

    return (
      <YStack flex={1} position="relative" maxWidth={maxWidth} width={"100%"}>
        {label && (
          <Label
            font={size}
            maxWidth={getLableMaxWidth(
              inputFrame.width,
              variant === "inline" ? 60 : 100,
            )}
            variant={variant}
            disabled={disabled}
            {...stylex([
              isError,
              {
                color: "$error-500",
              },
            ])}
          >
            {label}
          </Label>
        )}
        {secureTextEntry && (
          <Button
            unstyled
            padding={0}
            backgroundColor={"transparent"}
            borderWidth={0}
            cursor="pointer"
            height={"$2.5"}
            position="absolute"
            right={"$2.5"}
            bottom={resolveShowPasswordButtonYAxisPosition(
              size,
              !!isMessage,
              !!isError,
            )}
            onPress={() => setPassVisible((state) => !state)}
            {...(isWeb && {
              type: "button",
            })}
          >
            {!passVisible ? <OpenEye /> : <ClosedEye />}
          </Button>
        )}

        <Input
          onLayout={(e) =>
            setInputFrame({
              width: e.nativeEvent.layout.width,
            })
          }
          secureTextEntry={passVisible}
          ref={ref}
          variant={variant}
          size={size}
          {...stylex([
            isError && variant === "inline",
            {
              borderColor: "$gray-primary-900",
              color: "$gray-800",
            },
          ])}
          {...rest}
        />
        {isError && (
          <ErrorMessage
            variant={variant}
            maxWidth={getLableMaxWidth(
              inputFrame.width,
              variant === "inline" ? 85 : 100,
            )}
          >
            {error}
          </ErrorMessage>
        )}
        {!isError && isMessage ? (
          <BottomMessage maxWidth={getLableMaxWidth(inputFrame.width, 95)}>
            {message}
          </BottomMessage>
        ) : null}
      </YStack>
    );
  },
);

InputWithLabel.displayName = "InputWithLabel";
